import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  TrendingUp, RefreshCw, Undo2, Search, ChevronDown, ChevronRight,
  Percent, IndianRupee, Users, Calendar, FileText, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_LABELS = {
  basic_pay: "Basic Pay",
  da: "DA",
  hra: "HRA",
  conveyance: "Conveyance",
  other_allowances: "Other Allowances",
  daily_rate: "Daily Rate",
  monthly_salary: "Monthly Salary",
};

const HRIncrements = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [revertId, setRevertId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/hr/increments`);
      setItems(res.data);
    } catch {
      toast.error("Failed to load increments");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const refresh = async () => {
    setRefreshing(true); await fetchData(); setRefreshing(false);
  };

  const doRevert = async () => {
    if (!revertId) return;
    try {
      await axios.delete(`${API}/hr/increments/${revertId}`);
      toast.success("Increment reverted — salaries restored");
      setRevertId(null);
      fetchData();
    } catch {
      toast.error("Failed to revert");
    }
  };

  const filtered = items.filter(i => {
    if (!search) return true;
    const s = search.toLowerCase();
    if (i.note?.toLowerCase().includes(s)) return true;
    if (i.effective_date?.toLowerCase().includes(s)) return true;
    return (i.breakdown || []).some(b => b.employee_name?.toLowerCase().includes(s));
  });

  const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return iso; }
  };

  return (
    <div className="space-y-5" data-testid="hr-increments-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="h-8 w-1 bg-gradient-to-b from-amber-400 to-orange-400 rounded-full" />
            <h2 className="text-2xl font-bold text-amber-300 flex items-center gap-2">
              <TrendingUp className="h-6 w-6" /> Salary Increments History
            </h2>
          </div>
          <p className="text-gray-400 text-sm ml-3 mt-1">
            {items.length} increment run(s) · Revert anytime to restore old values
          </p>
        </div>
        <Button onClick={refresh} variant="outline" disabled={refreshing} className="border-gray-600 text-gray-300 hover:bg-gray-800" data-testid="btn-refresh">
          <RefreshCw className={cn("h-4 w-4 mr-2", refreshing && "animate-spin")} />
          Refresh
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by employee, note, or date..."
          className="pl-9 bg-gray-900/40 border-gray-700 text-white h-9"
          data-testid="search-input"
        />
      </div>

      <Card className="bg-gray-900/40 border-gray-800">
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-gray-400">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent" />
              <p className="mt-3 text-sm">Loading...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <TrendingUp className="mx-auto h-14 w-14 text-gray-700" />
              <p className="text-gray-400 mt-3">No salary increments yet</p>
              <p className="text-gray-500 text-xs mt-1">
                Apply your first increment from the <span className="text-teal-300">Employees &amp; Salary</span> page.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-800">
              {filtered.map(inc => (
                <Collapsible key={inc.id} open={expandedId === inc.id} onOpenChange={(o) => setExpandedId(o ? inc.id : null)}>
                  <div className="p-3 hover:bg-gray-800/30 transition-colors" data-testid={`inc-row-${inc.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="p-1 h-7 w-7 shrink-0">
                            {expandedId === inc.id ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                          </Button>
                        </CollapsibleTrigger>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className={cn("text-[10px]", inc.hike_type === "percent" ? "bg-blue-500/15 text-blue-300 border-blue-500/40" : "bg-emerald-500/15 text-emerald-300 border-emerald-500/40")}>
                              {inc.hike_type === "percent" ? <Percent className="h-3 w-3 inline mr-0.5" /> : <IndianRupee className="h-3 w-3 inline mr-0.5" />}
                              {inc.hike_type === "percent" ? `+${inc.value}%` : `+₹${fmt(inc.value)}`}
                            </Badge>
                            <Badge variant="outline" className={cn("text-[10px]", inc.is_bulk ? "bg-amber-500/15 text-amber-300 border-amber-500/40" : "bg-teal-500/15 text-teal-300 border-teal-500/40")}>
                              <Users className="h-3 w-3 mr-0.5" /> {inc.is_bulk ? "Bulk" : "Single"} · {inc.applied_count}
                            </Badge>
                            <Badge variant="outline" className="text-[10px] bg-gray-500/15 text-gray-300 border-gray-500/40">
                              <Calendar className="h-3 w-3 mr-0.5" /> {fmtDate(inc.effective_date)}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                            <span>{(inc.fields || []).map(f => FIELD_LABELS[f] || f).join(" · ")}</span>
                            {inc.note && (
                              <span className="italic truncate"><FileText className="h-3 w-3 inline mr-1" />{inc.note}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10 h-8 px-2 text-xs" onClick={() => setRevertId(inc.id)} data-testid={`revert-${inc.id}`}>
                        <Undo2 className="h-3.5 w-3.5 mr-1" /> Revert
                      </Button>
                    </div>
                  </div>
                  <CollapsibleContent>
                    <div className="px-4 pb-4 pt-1 bg-black/30 border-t border-gray-800/60">
                      <div className="bg-gray-900/60 rounded border border-gray-800 overflow-hidden">
                        <div className="max-h-72 overflow-y-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="border-gray-800 hover:bg-transparent">
                                <TableHead className="text-gray-500 text-[10px] uppercase h-8">Employee</TableHead>
                                <TableHead className="text-gray-500 text-[10px] uppercase h-8">Field</TableHead>
                                <TableHead className="text-right text-gray-500 text-[10px] uppercase h-8">Old</TableHead>
                                <TableHead className="text-gray-500 text-[10px] uppercase h-8 w-8"></TableHead>
                                <TableHead className="text-right text-gray-500 text-[10px] uppercase h-8">New</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {(inc.breakdown || []).flatMap(b =>
                                Object.entries(b.changes || {}).map(([field, chg]) => (
                                  <TableRow key={`${inc.id}_${b.employee_name}_${field}`} className="border-gray-800 hover:bg-gray-800/30">
                                    <TableCell className="text-gray-300 text-xs py-1.5">{b.employee_name}</TableCell>
                                    <TableCell className="text-gray-400 text-xs py-1.5">{FIELD_LABELS[field] || field}</TableCell>
                                    <TableCell className="text-right text-gray-400 font-mono text-xs py-1.5">₹{fmt(chg.old)}</TableCell>
                                    <TableCell className="py-1.5 text-amber-400"><ArrowRight className="h-3 w-3" /></TableCell>
                                    <TableCell className="text-right text-emerald-300 font-mono text-xs py-1.5 font-semibold">₹{fmt(chg.new)}</TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!revertId} onOpenChange={() => setRevertId(null)}>
        <AlertDialogContent className="bg-gray-900 border-red-500/50 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">Revert Increment?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              All affected employees will have their salaries restored to the pre-increment values. This also removes the increment from history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-600 text-gray-300 hover:bg-gray-800">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doRevert} className="bg-red-600 hover:bg-red-700" data-testid="confirm-revert">Revert</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default HRIncrements;
